import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Button, Stack, Typography, Alert, Skeleton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { botsApi } from "../../../api/bots.js";
import { useChat } from "../../../chat/useChat.js";
import type { Message } from "../../../chat/useChat.js";
import { ChatWindow } from "../../../chat/ChatWindow.js";
import { SUGGEST_TRIGGERS_PER_BOT_MAX } from "@chatfab/shared";
import type { Bot, SuggestedTrigger } from "@chatfab/shared";

// ── Playground session + message persistence ──────────────────────────────────
// Test sessions expire after 24 h (server-enforced). We persist the session id
// and message history in localStorage so users keep their conversation across
// tab switches, page refreshes, and app navigation. "New session" clears both.

interface StoredSession { session_id: string; created_at: number }
const SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const sessionKey  = (botId: string) => `playground_session_${botId}`;
const messagesKey = (botId: string) => `playground_messages_${botId}`;

function loadStoredSession(botId: string): string | null {
  try {
    const raw = localStorage.getItem(sessionKey(botId));
    if (!raw) return null;
    const s: StoredSession = JSON.parse(raw);
    if (Date.now() - s.created_at >= SESSION_MAX_AGE_MS) {
      localStorage.removeItem(sessionKey(botId));
      localStorage.removeItem(messagesKey(botId));
      return null;
    }
    return s.session_id;
  } catch {
    return null;
  }
}

function loadStoredMessages(botId: string): Message[] {
  try {
    const raw = localStorage.getItem(messagesKey(botId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveStoredSession(botId: string, sessionId: string): void {
  try {
    localStorage.setItem(sessionKey(botId), JSON.stringify({ session_id: sessionId, created_at: Date.now() }));
  } catch { /* localStorage unavailable */ }
}

function saveStoredMessages(botId: string, messages: Message[]): void {
  try {
    localStorage.setItem(messagesKey(botId), JSON.stringify(messages.filter(m => !m.pending)));
  } catch { /* quota exceeded — degrade gracefully */ }
}

function clearStoredSession(botId: string): void {
  try {
    localStorage.removeItem(sessionKey(botId));
    localStorage.removeItem(messagesKey(botId));
  } catch { /* */ }
}

// ── Components ────────────────────────────────────────────────────────────────

function PlaygroundChat({ sessionId, bot, onReset, initialMessages, suggestedTriggers }: {
  sessionId: string;
  bot: Bot;
  onReset: () => void;
  initialMessages: Message[];
  suggestedTriggers: SuggestedTrigger[];
}) {
  const { messages, status, capMessage, sendMessage, sendSuggestedTrigger } = useChat({
    sessionId,
    botId: bot.bot_id,
    initialMessages,
  });

  // Persist messages to localStorage after each completed turn (no pending messages).
  useEffect(() => {
    if (messages.length > 0 && !messages.some(m => m.pending)) {
      saveStoredMessages(bot.bot_id, messages);
    }
  }, [messages, bot.bot_id]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" px={1.5} py={1} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography variant="caption" color="text.secondary">
          Session: <code style={{ fontSize: 11 }}>{sessionId.slice(0, 8)}…</code>
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={onReset}>
          New session
        </Button>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ChatWindow
          messages={messages}
          status={status}
          capMessage={capMessage}
          onSend={sendMessage}
          botName={bot.name}
          welcomeMessage={bot.embed_config?.welcome_message}
          theme={bot.embed_config?.widget_theme}
          showBranding={false}
          suggestedTriggers={suggestedTriggers}
          onSuggestedTrigger={sendSuggestedTrigger}
        />
      </Box>
    </Box>
  );
}

export function PlaygroundTab({ botId, bot }: { botId: string; bot: Bot }) {
  // Lazy-init from localStorage — avoids a loading flash when a session exists.
  const [sessionId, setSessionId]             = useState<string | null>(() => loadStoredSession(botId));
  const [initialMessages, setInitialMessages] = useState<Message[]>(() => {
    const stored = loadStoredSession(botId);
    return stored ? loadStoredMessages(botId) : [];
  });
  const [loading, setLoading]                 = useState<boolean>(() => !loadStoredSession(botId));
  const [error, setError]                     = useState<string | null>(null);
  const [suggested, setSuggested]             = useState<SuggestedTrigger[]>([]);

  // Pull the workflow so the playground shows the same suggested chips users
  // will see on the public chat — lets the customer test chip clicks end to end.
  useEffect(() => {
    let cancelled = false;
    botsApi.getWorkflow(botId).then(data => {
      if (cancelled) return;
      const wf = data.workflow;
      if (!wf?.enabled) { setSuggested([]); return; }
      const list = (wf.triggers ?? [])
        .filter(t => t.suggest_in_chat && (t.suggest_label ?? "").trim())
        .slice(0, SUGGEST_TRIGGERS_PER_BOT_MAX)
        .map(t => ({ trigger_id: t.id, label: (t.suggest_label ?? "").trim() }));
      setSuggested(list);
    }).catch(() => setSuggested([]));
    return () => { cancelled = true; };
  }, [botId]);

  const createFreshSession = useCallback(async () => {
    clearStoredSession(botId);
    setLoading(true);
    setError(null);
    setSessionId(null);
    setInitialMessages([]);
    try {
      const { session_id } = await botsApi.createTestSession(botId);
      saveStoredSession(botId, session_id);
      setSessionId(session_id);
    } catch (e: any) {
      setError(e.message ?? "Failed to create test session");
    } finally {
      setLoading(false);
    }
  }, [botId]);

  // If no session was restored from storage, create one on mount.
  useEffect(() => {
    if (!sessionId) {
      createFreshSession();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (bot.state !== "active") {
    return (
      <Alert severity="warning">
        This bot is paused. Activate it to use the playground.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Test your bot exactly as your users experience it. Your session persists across page refreshes and tab navigation — use "New session" to start fresh.
      </Typography>

      <Box sx={{
        height: "calc(100vh - 280px)",
        minHeight: 400,
        width: "min(50vw, 800px)",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxShadow: 3,
      }}>
        {loading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rounded" height={48} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" height={320} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button variant="outlined" onClick={createFreshSession}>Retry</Button>
          </Box>
        ) : sessionId ? (
          <PlaygroundChat
            key={sessionId}
            sessionId={sessionId}
            bot={bot}
            onReset={createFreshSession}
            initialMessages={initialMessages}
            suggestedTriggers={suggested}
          />
        ) : null}
      </Box>
    </Box>
  );
}
